import React, { useEffect, useState } from 'react';
import { Avatar, Group, MultiSelect, Select, Text } from '@mantine/core';
import { DEFAULT_CONTAINER_TYPE_ICON, getImageUrl } from '@/utils/imageUrl';
import { ContainerType } from '../../views/Events/OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';

export interface ContainerTypeSelectorItemProps
  extends React.ComponentProps<'div'> {
  image: string;
  label: string;
  ct_id: number;
  ct_class: string;
  ct_group: string;
  ct_hexCode: string;
  value: string;
}

const SelectContainertTypeItem = ({
  ref,
  image,
  label,
  ct_id,
  ct_class,
  ct_group,
  ct_hexCode,
  ...others
}: ContainerTypeSelectorItemProps) => (
  <div ref={ref} {...others}>
    <Group noWrap>
      <Avatar src={getImageUrl(40, image)} />
      <div>
        <Text size="sm">
          <span style={{ color: `#${ct_hexCode}` }}>&#9632;</span> {label}
        </Text>
        <Text size="xs" opacity={0.65}>
          Id: {ct_id} | Group: {ct_group}
        </Text>
        <Text size="xs" opacity={0.65}>
          Class: {ct_class}
        </Text>
      </div>
    </Group>
  </div>
);

const containerTypeListToSlector = (containerTypes: ContainerType[]) => {
  return [...containerTypes]
    .sort((a, b) => a.id - b.id)
    .map((ct) => {
      return {
        image: ct.imageUrl || DEFAULT_CONTAINER_TYPE_ICON,
        label: ct.name,
        ct_id: ct.id,
        ct_class: ct.class.toLowerCase().trim(),
        ct_group: ct.group.toLowerCase().trim(),
        ct_hexCode: ct.hexCode,
        value: ct.id.toString(),
      };
    });
};

export const defaultContainerTypeFilter = (
  value: string,
  item: ContainerTypeSelectorItemProps,
) => {
  const valueTrimmed = value.toLowerCase().trim();
  const labelTrimmed = item?.label?.toLowerCase()?.trim();

  return (
    (labelTrimmed && labelTrimmed.includes(valueTrimmed)) ||
    item?.ct_id.toString()?.includes(valueTrimmed) ||
    item?.ct_class?.includes(valueTrimmed) ||
    item?.ct_group?.includes(valueTrimmed) ||
    false
  );
};

type ContainerTypeSelectorProps = {
  className?: string;
  data: ContainerType[];
  value: string | string[];
  searchable?: boolean;
  clearable?: boolean;
  required?: boolean;
  onChange: (value: string | string[] | null) => void;
  onFilter?: (value: string, item: ContainerTypeSelectorItemProps) => boolean;
  defaultFilterValue?: string;
};

const ContainerTypeSelector = ({
  className,
  data,
  value,
  searchable = false,
  clearable = false,
  required = false,
  onChange,
  onFilter,
  defaultFilterValue,
}: ContainerTypeSelectorProps) => {
  const [filterValue, setFilterValue] = useState(defaultFilterValue);
  useEffect(() => {
    setFilterValue(defaultFilterValue);
  }, [defaultFilterValue]);

  const combinedFilter = (
    value: string,
    item: ContainerTypeSelectorItemProps,
  ) => {
    const currentFilterValue = value || filterValue || ''; // Use state if input is empty

    if (!onFilter) {
      return defaultContainerTypeFilter(currentFilterValue, item);
    }

    return onFilter(currentFilterValue, item);
  };

  if (Array.isArray(value)) {
    return (
      <MultiSelect
        className={className}
        data={containerTypeListToSlector(data)}
        itemComponent={SelectContainertTypeItem}
        value={value}
        onChange={(value) => {
          onChange(value);
        }}
        filter={(
          value: string,
          selected: boolean,
          item: ContainerTypeSelectorItemProps,
        ) => {
          if (!onFilter) {
            return defaultContainerTypeFilter(value, item);
          }

          return onFilter(value, item);
        }}
        searchable={searchable}
        clearable={clearable}
        required={required}
      />
    );
  }

  return (
    <Select
      className={className}
      data={containerTypeListToSlector(data)}
      itemComponent={SelectContainertTypeItem}
      value={value}
      onChange={(value) => {
        onChange(value);
      }}
      filter={combinedFilter}
      searchable={searchable}
      clearable={clearable}
      required={required}
    />
  );
};

export default ContainerTypeSelector;
